import { ILaya } from "../../../../ILaya";
import { RenderClearFlag } from "../../../RenderEngine/RenderEnum/RenderClearFlag";
import { RenderTargetFormat } from "../../../RenderEngine/RenderEnum/RenderTargetFormat";
import { Camera, CameraClearFlags, CameraEventFlags } from "../../../d3/core/Camera";
import { ShadowMode } from "../../../d3/core/light/ShadowMode";
import { RenderContext3D } from "../../../d3/core/render/RenderContext3D";
import { Scene3D } from "../../../d3/core/scene/Scene3D";
import { Scene3DShaderDeclaration } from "../../../d3/core/scene/Scene3DShaderDeclaration";
import { DepthPass } from "../../../d3/depthMap/DepthPass";
import { ShadowCasterPass } from "../../../d3/shadowMap/ShadowCasterPass";
import { LayaGL } from "../../../layagl/LayaGL";
import { StatElement } from "../../../layagl/StatisticsContext";
import { Vector4 } from "../../../maths/Vector4";
import { Viewport } from "../../../maths/Viewport";
import { DepthTextureMode } from "../../../resource/RenderTexture";
import { Browser } from "../../../utils/Browser";
import { Stat } from "../../../utils/Stat";
import { WebGLForwardAddRP } from "./WebGLForwardAddRP";
const viewport = new Viewport(0, 0, 0, 0);
const offsetScale = new Vector4();
export class WebGLRender3DProcess {
    constructor() {
        this.renderpass = new WebGLForwardAddRP();
    }
    initRenderpass(camera, context) {
        let renderpass = this.renderpass.renderpass;
        let renderRT = camera._getRenderTexture();
        let clearConst = 0;
        let clearFlag = camera.clearFlag;
        if (clearFlag == CameraClearFlags.Sky && !camera.scene.skyRenderer._isAvailable()) {
            clearFlag = CameraClearFlags.SolidColor;
        }
        let hasStencil = renderRT.depthStencilFormat == RenderTargetFormat.DEPTHSTENCIL_24_8;
        let stencilFlag = hasStencil ? RenderClearFlag.Stencil : 0;
        switch (clearFlag) {
            case CameraClearFlags.DepthOnly:
            case CameraClearFlags.Sky:
                clearConst = RenderClearFlag.Depth | stencilFlag;
                break;
            case CameraClearFlags.Nothing:
                clearConst = 0;
                break;
            case CameraClearFlags.ColorOnly:
                clearConst = RenderClearFlag.Color;
                break;
            case CameraClearFlags.SolidColor:
            default:
                clearConst = RenderClearFlag.Color | RenderClearFlag.Depth | stencilFlag;
                break;
        }
        let clearValue = camera._linearClearColor;
        clearValue = renderRT.gammaCorrection != 1 ? camera.clearColor : camera._linearClearColor;
        renderpass.camera = camera._renderDataModule;
        renderpass.destTarget = renderRT._renderTarget;
        renderpass.clearFlag = clearConst;
        renderpass.clearColor = clearValue;
        let needInternalRT = camera._needInternalRenderTexture();
        if (needInternalRT) {
            viewport.set(0, 0, renderRT.width, renderRT.height);
        }
        else {
            camera.viewport.cloneTo(viewport);
        }
        renderpass.setViewPort(viewport);
        let scissor = Vector4.TEMP;
        scissor.setValue(viewport.x, viewport.y, viewport.width, viewport.height);
        renderpass.setScissor(scissor);
        renderpass.enableOpaque = Stat.enableOpaque;
        renderpass.enableTransparent = Stat.enableTransparent;
        renderpass.enableCMD = Stat.enableCameraCMD;
        renderpass.setBeforeSkyboxCmds(camera._cameraEventCommandBuffer[CameraEventFlags.BeforeSkyBox]);
        renderpass.setBeforeForwardCmds(camera._cameraEventCommandBuffer[CameraEventFlags.BeforeForwardOpaque]);
        renderpass.setBeforeTransparentCmds(camera._cameraEventCommandBuffer[CameraEventFlags.BeforeTransparent]);
        this.renderpass.setBeforeImageEffect(camera._cameraEventCommandBuffer[CameraEventFlags.BeforeImageEffect]);
        this.renderpass.setAfterEventCmd(camera._cameraEventCommandBuffer[CameraEventFlags.AfterEveryThing]);
        renderpass.setCameraCullInfo(camera);
        if (clearFlag == CameraClearFlags.Sky) {
            renderpass.skyRenderNode = camera.scene.skyRenderer._baseRenderNode;
        }
        else {
            renderpass.skyRenderNode = null;
        }
        renderpass.pipelineMode = RenderContext3D._instance.configPipeLineMode;
        let enableShadow = Scene3D._updateMark % camera.scene._ShadowMapupdateFrequency == 0 && Stat.enableShadow;
        this.renderpass.shadowCastPass = enableShadow;
        if (enableShadow) {
            let shadowParams = this.renderpass.shadowParams;
            shadowParams.setValue(0, 0, 0, 0);
            let sceneShaderData = context.sceneData;
            let mainDirectionLight = camera.scene._mainDirectionLight;
            let needDirectionShadow = mainDirectionLight && mainDirectionLight.shadowMode != ShadowMode.None;
            this.renderpass.enableDirectLightShadow = needDirectionShadow;
            if (needDirectionShadow) {
                this.renderpass.directLightShadowPass.camera = camera._renderDataModule;
                this.renderpass.directLightShadowPass.light = mainDirectionLight._dataModule;
                let directionShadowMap = ILaya.Scene3D._shadowCasterPass.getDirectLightShadowMap(mainDirectionLight);
                this.renderpass.directLightShadowPass.destTarget = directionShadowMap._renderTarget;
                shadowParams.x = this.renderpass.directLightShadowPass.light.shadowStrength;
                sceneShaderData.setTexture(ShadowCasterPass.SHADOW_MAP, directionShadowMap);
            }
            let mainSpotLight = camera.scene._mainSpotLight;
            let needSpotShadow = mainSpotLight && mainSpotLight.shadowMode != ShadowMode.None;
            this.renderpass.enableSpotLightShadowPass = needSpotShadow;
            if (needSpotShadow) {
                this.renderpass.spotLightShadowPass.light = mainSpotLight._dataModule;
                let spotShadowMap = ILaya.Scene3D._shadowCasterPass.getSpotLightShadowPassData(mainSpotLight);
                this.renderpass.spotLightShadowPass.destTarget = spotShadowMap._renderTarget;
                shadowParams.y = this.renderpass.spotLightShadowPass.light.shadowStrength;
                sceneShaderData.setTexture(ShadowCasterPass.SHADOW_SPOTMAP, spotShadowMap);
            }
            sceneShaderData.setVector(ShadowCasterPass.SHADOW_PARAMS, this.renderpass.shadowParams);
        }
        renderpass.blitOpaqueBuffer.clear();
        let needBlitOpaque = camera.opaquePass;
        renderpass.enableOpaqueTexture = needBlitOpaque;
        if (needBlitOpaque) {
            renderpass.opaqueTexture = camera._opaqueTexture._renderTarget;
            renderpass.blitOpaqueBuffer.blitScreenQuad(renderRT, camera._opaqueTexture);
        }
        if (Stat.enablePostprocess && camera.postProcess && camera.postProcess.enable && camera.postProcess.effects.length > 0) {
            this.renderpass.enablePostProcess = Stat.enablePostprocess;
            this.renderpass.postProcess = camera.postProcess._context.command;
            camera.postProcess._render(camera);
            this.renderpass.postProcess._apply(false);
        }
        else {
            this.renderpass.enablePostProcess = false;
        }
        this.renderpass.finalize.clear();
        if (!this.renderpass.enablePostProcess && needInternalRT && camera._offScreenRenderTexture) {
            let dst = camera._offScreenRenderTexture;
            offsetScale.setValue(camera.normalizedViewport.x, 1.0 - camera.normalizedViewport.y, renderRT.width / dst.width, -renderRT.height / dst.height);
            this.renderpass.finalize.blitScreenQuad(renderRT, camera._offScreenRenderTexture, offsetScale);
        }
    }
    renderDepth(camera) {
        let depthMode = camera.depthTextureMode;
        if (camera.postProcess && camera.postProcess.enable) {
            depthMode |= camera.postProcess.cameraDepthTextureMode;
        }
        if ((depthMode & DepthTextureMode.Depth) != 0) {
            Camera.depthPass.getTarget(camera, DepthTextureMode.Depth, camera.depthTextureFormat);
            this.renderpass.renderpass.depthTarget = camera.depthTexture._renderTarget;
            Camera.depthPass._setupDepthModeShaderValue(DepthTextureMode.Depth, camera);
        }
        if ((depthMode & DepthTextureMode.DepthNormals) != 0) {
            Camera.depthPass.getTarget(camera, DepthTextureMode.DepthNormals, camera.depthTextureFormat);
            this.renderpass.renderpass.depthNormalTarget = camera.depthNormalTexture._renderTarget;
            camera._shaderValues.setTexture(DepthPass.DEPTHNORMALSTEXTURE, camera.depthNormalTexture);
            Camera.depthPass._setupDepthModeShaderValue(DepthTextureMode.DepthNormals, camera);
        }
        this.renderpass.renderpass.depthTextureMode = depthMode;
    }
    fowardRender(context, camera) {
        Camera.depthPass.cleanUp(camera);
        this.renderDepth(camera);
        this.initRenderpass(camera, context);
        let renderList = this.render3DManager.baseRenderList.elements;
        let count = this.render3DManager.baseRenderList.length;
        this.renderFowarAddCameraPass(context, this.renderpass, renderList, count);
    }
    renderFowarAddCameraPass(context, renderpass, list, count) {
        var time = Browser.now();
        if (renderpass.shadowCastPass) {
            if (renderpass.enableDirectLightShadow) {
                context.sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW);
                context.sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT);
                renderpass.directLightShadowPass.update(context);
                renderpass.directLightShadowPass.render(context, list, count);
            }
            if (renderpass.enableSpotLightShadowPass) {
                context.sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT);
                context.sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW);
                renderpass.spotLightShadowPass.update(context);
                renderpass.spotLightShadowPass.render(context, list, count);
            }
            if (renderpass.enableDirectLightShadow) {
                context.sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW);
            }
            else {
                context.sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW);
            }
            if (renderpass.enableSpotLightShadowPass) {
                context.sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT);
            }
            else {
                context.sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT);
            }
        }
        LayaGL.statAgent.recordTimeData(StatElement.T_ShadowPass, Browser.now() - time);
        renderpass.renderpass.render(context, list, count);
        renderpass._beforeImageEffectCMDS && this._rendercmd(renderpass._beforeImageEffectCMDS, context);
        if (renderpass.enablePostProcess) {
            time = Browser.now();
            renderpass.postProcess && this._renderPostProcess(renderpass.postProcess, context);
            LayaGL.statAgent.recordTimeData(StatElement.T_Render_PostProcess, Browser.now() - time);
        }
        renderpass._afterAllRenderCMDS && this._rendercmd(renderpass._afterAllRenderCMDS, context);
        renderpass.finalize._apply(false);
        context.runCMDList(renderpass.finalize._renderCMDs);
    }
    _rendercmd(cmds, context) {
        if (!cmds || cmds.length == 0)
            return;
        cmds.forEach(function (value) {
            context.runCMDList(value._renderCMDs);
        });
    }
    _renderPostProcess(postprocessCMD, context) {
        context.runCMDList(postprocessCMD._renderCMDs);
    }
    destroy() {
        this.renderpass.destroy();
    }
}
